import { CurrencyInCents, currencyInCentsToString, formatCurrency } from "@sp-crm/core";
import {
    GetClientQuery,
    GetCommunityQuery,
    InvoiceLineItemInput,
    InvoicePaymentScheduleInput,
} from "generated/graphql";
import {
    ApiCommission,
    CommissionDraft,
    diffCommissionDrafts,
    totalCommissionBasisPoints,
} from "./invoice-commissions-editor";
import { SplitRuleDraft, totalSplitRuleBasisPoints } from "./invoice-split-math";

export type ClientInvoice = NonNullable<GetClientQuery["getClient"]["invoices"]>[0];
export type CommunityInvoice = NonNullable<
    GetCommunityQuery["getCommunity"]["invoices"]
>[0];

export const paymentScheduleHasChanged = (
    original: InvoicePaymentScheduleInput | null,
    current: InvoicePaymentScheduleInput | null,
): boolean =>
    JSON.stringify(original?.items ?? null) !== JSON.stringify(current?.items ?? null);

export const lineItemsTotalInCents = (lineItems: InvoiceLineItemInput[] | null): number =>
    (lineItems || []).reduce(
        (total, lineItem) => total + lineItem.quantity * lineItem.unitPriceInCents,
        0,
    );

export const validatePaymentSchedule = (
    lineItems: InvoiceLineItemInput[] | null,
    paymentSchedule: InvoicePaymentScheduleInput | null,
): string[] => {
    const errors: string[] = [];

    if (!paymentSchedule) {
        return errors;
    }

    const totalLineItemAmountInCents = lineItemsTotalInCents(lineItems);

    const totalPaymentScheduleAmountInCents = paymentSchedule.items.reduce(
        (total, paymentScheduleItem) => {
            return total + paymentScheduleItem.amountInCents;
        },
        0,
    );

    if (totalLineItemAmountInCents !== totalPaymentScheduleAmountInCents) {
        errors.push(
            `The payment schedule total amount (${formatCurrency(
                currencyInCentsToString(
                    totalPaymentScheduleAmountInCents as CurrencyInCents,
                ),
            )}) needs to match the invoice total amount (${formatCurrency(
                currencyInCentsToString(totalLineItemAmountInCents as CurrencyInCents),
            )})`,
        );
    }

    if (!paymentSchedule.items.length) {
        errors.push(`The payment schedule must have at least one payment`);
    }

    if (!paymentSchedule.items.every(item => !!item.dueDate)) {
        errors.push(`All scheduled payments must have a due date`);
    }

    return errors;
};

export interface SaveWarning {
    title: string;
    message: string;
}

export const collectCommissionPercentageWarning = (args: {
    employeeCommissionsSectionVisible: boolean;
    commissionDrafts: CommissionDraft[];
    splitFeatureEnabled: boolean;
    splitDrafts: SplitRuleDraft[];
}): SaveWarning[] => {
    const splitsBasisPoints = args.splitFeatureEnabled
        ? totalSplitRuleBasisPoints(args.splitDrafts)
        : 0;
    const commissionsBasisPoints = args.employeeCommissionsSectionVisible
        ? totalCommissionBasisPoints(args.commissionDrafts)
        : 0;
    const combinedBasisPoints = splitsBasisPoints + commissionsBasisPoints;
    if (combinedBasisPoints < 10000) {
        return [];
    }

    const combinedPercent = Number((combinedBasisPoints / 100).toFixed(2));
    const subject = [
        args.splitFeatureEnabled ? "splits" : null,
        args.employeeCommissionsSectionVisible ? "commissions" : null,
    ]
        .filter((label): label is string => label !== null)
        .join(" and ");
    const capitalizedSubject = subject.charAt(0).toUpperCase() + subject.slice(1);
    return [
        {
            title: "Check the percentages",
            message: `${capitalizedSubject} on this invoice add up to ${combinedPercent}% of the invoice total.`,
        },
    ];
};

export const collectCreateSaveWarnings = (args: {
    employeeCommissionsSectionVisible: boolean;
    commissionDrafts: CommissionDraft[];
}): SaveWarning[] =>
    collectCommissionPercentageWarning({
        employeeCommissionsSectionVisible: args.employeeCommissionsSectionVisible,
        commissionDrafts: args.commissionDrafts,
        splitFeatureEnabled: false,
        splitDrafts: [],
    });

export const collectSaveWarnings = (args: {
    employeeCommissionsSectionVisible: boolean;
    invoiceTotalPaidCents: number;
    originalCommissions: ApiCommission[];
    commissionDrafts: CommissionDraft[];
    originalPaymentSchedule: InvoicePaymentScheduleInput | null;
    currentPaymentSchedule: InvoicePaymentScheduleInput | null;
    splitFeatureEnabled: boolean;
    splitDrafts: SplitRuleDraft[];
}): SaveWarning[] => {
    const warnings: SaveWarning[] = [];

    if (args.employeeCommissionsSectionVisible && args.invoiceTotalPaidCents > 0) {
        const commissionDiff = diffCommissionDrafts(
            args.originalCommissions,
            args.commissionDrafts,
        );
        if (commissionDiff.toCreate.length > 0) {
            const collectedAmount = formatCurrency(args.invoiceTotalPaidCents / 100);
            warnings.push({
                title: "New commission on a paid invoice",
                message: `This invoice already has ${collectedAmount} collected. Saving will immediately create a commission accrual for the new team member against that amount.`,
            });
        }
    }

    if (
        args.employeeCommissionsSectionVisible &&
        paymentScheduleHasChanged(
            args.originalPaymentSchedule,
            args.currentPaymentSchedule,
        ) &&
        args.commissionDrafts.some(d => d.locked)
    ) {
        warnings.push({
            title: "Payment schedule change on an invoice with a paid commission",
            message:
                "This invoice has a commission that's already been marked paid. Changing the payment schedule won't change what's already been paid out, but it may change what's still owed on other commissions for this invoice.",
        });
    }

    warnings.push(
        ...collectCommissionPercentageWarning({
            employeeCommissionsSectionVisible: args.employeeCommissionsSectionVisible,
            commissionDrafts: args.commissionDrafts,
            splitFeatureEnabled: args.splitFeatureEnabled,
            splitDrafts: args.splitDrafts,
        }),
    );

    return warnings;
};
